import _ from 'lodash';
import Element from '../ui/Element';
import * as members from '../privates/privateMembers';
import platformUtils from '../privates/platformUtils';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import mixin from '../privates/utils/mixin';
import {getShouldClearState} from '../privates/utils/livePreview';
import {validate, validateArrayContainsItemsWithIds} from '../privates/validations/validation';
import regexValidations from '../privates/validations/regexValidations';
import reporter from '../privates/reporter';
import {generateError} from '../privates/validations/errors';
import compInitializeService from '../privates/compInitializeService/compInitializeService';
import monitoring from '../privates/monitoring';
import * as postMessage from '../privates/postMessage';


function getRepeaterOrDataItems(RMI, compId) {
    const compData = RMI.getData(compId);
    return compData.fullItems || _.map(compData.items, (itemId) => ({ _id: itemId }));
}

function executeBehaviorByTypeForItems(RMI, wixSelector, repeaterId, behaviorType, items, callWith$w, compName, functionName) {
    const componentEvents = RMI.getEvents(repeaterId);
    const filteredBehaviors = _.filter(componentEvents, {eventType: behaviorType});
    const compBehaviors = _.transform(filteredBehaviors, (result, behavior) => {
        const funcRef = behavior.callbackId;
        const dynamicCallback = RMI.getCallbackById(funcRef, true);
        const staticCallback = platformUtils.getStaticEventHandler(funcRef);
        const eventHandler = dynamicCallback || staticCallback;
        if(eventHandler) {
            result.push({cb: _.get(eventHandler, 'callback'), contextId: _.get(eventHandler, 'contextId'), controllerId: _.get(eventHandler, 'controllerId') || _.get(eventHandler, 'contextId')});
        }
    }, []);

    executeBehaviorForItems(wixSelector, repeaterId, compBehaviors, items, callWith$w, compName, functionName);
}

function logErrorFromFunction(compName, functionName, errorMessage) {
    reporter.reportSdkError(generateError.error_function_throws_error({
        compName,
        functionName,
        errorMessage
    }));
}

function executeBehaviorForItems(wixSelector, repeaterId, compBehaviors, items, callWith$w, compName, functionName) {
    if (_.isEmpty(items) || _.isEmpty(compBehaviors)) {
        return;
    }
    _.forEach(items, (item) => {
        const $wToContextAndController = {};
        _.forEach(compBehaviors, (behavior) => {
            const controllerId = behavior.controllerId;
            const contextId = behavior.contextId;
            if (!callWith$w) {
                try {
                    behavior.cb.call(this, item.item);
                } catch (e) {
                    logErrorFromFunction(compName, functionName, e.message);
                }
                return;
            }
            let $w = _.get($wToContextAndController, [contextId, controllerId]);
            if(!$w){
                $w = wixSelector.getScopedSelectorForDisplayedOnlyComp(repeaterId, item.item._id, contextId, controllerId);
                _.set($wToContextAndController, [contextId, controllerId], $w);
            }
            try {
                behavior.cb.call(this, $w, item.item, item.index);
            } catch (e) {
                logErrorFromFunction(compName, functionName, e.message);
            }
        });
    });
}

function getCompIdToCompType(RMI, repeaterId, reduceItems) {
    const repeaterUtils = platformUtils.getRepeaterUtils();
    return _.reduce(repeaterUtils.items.getTemplateCompsId(RMI, repeaterId, reduceItems),
        (result, compId) => {
            result[compId] = RMI.getType(compId);
            return result;
        }, {});
}

function validateItemIdFormatAndReportIfNecessary(id, index, repeaterId) {
    if (!regexValidations.validateRepeaterItemIdFormat(id)) {
        reporter.reportSdkError(generateError.error_bad_item_id_format({
            repeaterId,
            wrongValue: id,
            index
        }));

        return false;
    }
    return true;
}

function clearPrevStateIfNeeded(currentItems, newItems) {
    const repeaterUtils = platformUtils.getRepeaterUtils();
    const itemsDiff = repeaterUtils.items.getAddedItems(currentItems, newItems);
    if (getShouldClearState() && _.isEmpty(itemsDiff)) {
            return [];
    }
    return currentItems;
}

const MAX_ITEMS_EDITOR = 12;

/**
 * @class Repeater
 * @summary A repeating layout.
 * @classdesc
 *  Repeaters provide a way for you to add repeating content to a page.
 *  Repeaters consist of repeating items, each with the same layout
 *  but different data.
 *
 *  For example, the repeater below contains three items, each with the same
 *  layout. There is an image on the left and two text elements on the right.
 *  However, the data in each item is different. That is, the actual images and
 *  text values are different in each repeated item.
 *
 *  ![Repeater example](images/repeater.png "Repeater example")
 *
 *  The data displayed in a repeater comes from either:
 *
 *  + Connecting the repeater and the elements contained within its items to a
 *    dataset in the Editor.
 *  + Using the [`data`](#data) property in conjunction with the [`forEachItem()`](#forEachItem),
 *    [`forItems()`](#forItems), and [`onItemReady()`](#onItemReady) functions
 *    in code.
 *
 *
 *  ### Repeated Item Template
 *
 *  Each repeater has an item template that contains the elements and initial data
 *  that are used when new items are created. The template's initial state is the
 *  state of the first repeated item that appears in the Editor. Using code, you
 *  can set the properties of, get the properties of, or call functions on the
 *  elements of the item template by selecting the elements using
 *  [`$w()`]($w.html#w), the [global scope](#global-scope) selector function.
 *
 *  ### Selector Scope
 *
 *  Selector functions are used to select specific page elements so you can work
 *  with them in code. Depending on which selector you use, you are able to
 *  select elements from the different scopes described below.
 *
 *  There are two types of selector functions:
 *
 *  + [Global Scope Selectors](#global-scope)
 *  + [Repeated Item Scope Selectors](#repeated-item-scope)
 *
 *
 *  #### Global Scope
 * 
 *  The [`$w()`]($w.html#w) function that is available to your Page
 *  and Site code selects elements in the global scope.
 *
 *  A selector with global scope can be used to select any element that is not
 *  contained in a repeater. You can also use it to select an element that is
 *  contained in a repeater, but you need to understand what that selection means.
 *
 *  When you select an element contained in a repeater from the global scope and
 *  you get the value of one of the element's properties, you receive the value
 *  of that element's property from the repeater's [item template](#repeated-item-template).
 *
 *  For example, here `templateText` is the `text` value of the `myRepeatedText`
 *  element from the repeater's item template.
 *
 *  ```javascript
 *  $w.onReady( function () {
 *    let templateText = $w("#myRepeatedText").text;
 *  } );
 *  ```
 *
 *  When you select an element contained in a repeater from the global scope and
 *  you set the value of one of the element's properties or call one of the
 *  element's functions, the value is set or the function is called on the
 *  repeater's [item template](#repeated-item-template) **and** all repeated instances of
 *  that element.
 *
 *
 *  For example, here the item template is changed so that `"New Text"` is the
 *  `text` value of the `myRepeatedText` element. Also, all existing repeated
 *  items have the `text` value of their `myRepeatedText` element set to `"New Text"`.
 *
 *  ```javascript
 *  $w.onReady( function () {
 *    $w("#myRepeatedText").text = "New Text";
 *  } );
 *  ```
 *
 *  And here the item template is changed so that the `myRepeatedImage` element
 *  is hidden. Also, all existing repeated items have their `myRepeatedImage` element hidden.
 *
 *  ```javascript
 *  $w.onReady( function () {
 *    $w("#myRepeatedImage").hide();
 *  } );
 *  ```
 *
 *  #### Repeated Item Scope
 * 
 *  There are two instances where you get a repeated-item-scope selector:
 *
 *  + The `$item` parameter of the [`forEachItem()`](#forEachItem),
 *    [`forItems()`](#forItems), and [`onItemReady()`](#onItemReady) event
 *    handlers.
 *  + When calling the [$w.at()]($w.html#at) and passing it an event whose
 *    [`context`]($w.Event.html#context) is "COMPONENT_SCOPE". This is usually
 *    done in an event handler that handles event on an element inside a repeater.
 *
 *
 *  A selector with repeated item scope can be used to select a specific instance
 *  of a repeating element.
 *
 *  For example, here when the `myRepeatedImage` element is clicked, the value of
 *  a text element in the same repeated item where the image was clicked is changed
 *  to "Selected". All the other text elements elements with the ID `myRepeatedText`
 *  in the other items of the repeater are not affected.
 *
 *  ```javascript
 *  $w.onReady( function () {
 *    $w("#myRepeatedImage").onClick( (event) => {
 *      let $item = $w.at(event.context);
 *      $item("#myRepeatedText").text = "Selected";
 *    } );
 *  } );
 *  ```
 *
 *  And here, when each item is ready, the value of a text element is set to a
 *  value found in that specific item's data.
 *
 *  ```javascript
 *  $w("#myRepeater").onItemReady( ($item, itemData, index) => {
 *    $item("#myRepeatedText").text = itemData.textField;
 *  } );
 *  ```
 *
 *  You can also use a selector with repeated item scope to select non-repeating
 *  elements from the global scope. However, you cannot change a repeater's
 *  [item template](#repeated-item-template) using a selector with repeated item scope.
 *
 *  You can restrict a selector with repeated item scope to only select
 *  elements from the current repeated items and their descendants, but not elements
 *  from the global scope by adding `.scoped()` to the selector so the function
 *  call looks like `$item.scoped("#idToSelect")`.
 *
 *  ### Retrieve Repeater Item Data When Clicked
 *
 *  Each repeated item in a repeater has a [`Container`]($w.Container.html) element
 *  that holds all of its repeated elements. To retrieve the data associated
 *  with a specific repeated item when it is clicked, create an `onClick` event
 *  handler for the item's `Container`. Depending on how you populated the repeater
 *  with data, you either use the connected dataset or the repeater's `data`
 *  array to retrieve the clicked item's data in the event handler.
 *
 *  For a repeater populated by connecting it to a dataset:
 *
 *  ```javascript
 *  $w.onReady( function () {
 *    $w("#repeatedContainer").onClick( (event) => {
 *      let $item = $w.at(event.context);
 *      let clickedItemData = $item("#myDataset").getCurrentItem();
 *    } );
 *  } );
 *  ```
 *
 *  For a repeater populated by setting its [`data`](#data) property:
 *
 *  ```javascript
 *  $w.onReady( function () {
 *    $w("#repeatedContainer").onClick( (event) => {
 *      const data = $w("#myRepeater").data;
 *      let clickedItemData = data.find(item => item._id === event.context.itemId);
 *    } );
 *  } );
 *  ```
 *
 *
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.Repeater
 * @definitionId wysiwyg.viewer.components.Repeater
 * @tagname PLACEHOLDER
 */
class Repeater extends Element {

    /**
     * @member data
     * @label property
     * @summary Sets or gets the repeater data.
     * @description
     *  A repeater's data is stored as an array of objects. Each object in the array
     *  must contain a unique `_id` property which is used to match the object's
     *  data to the individual repeated items of the repeater as described below.
     *  The value of the `_id` property can only contain alphanumeric
     *  characters (A-Z, a-z, 0-9) and hyphens (`-`). Other than `_id`, the objects in the
     *  repeater's data array can contain anything you want.
     *
     *  For example, a simple array of repeater data may look like this:
     *
     *  ```javascript
     *  [
     *    {
     *      "_id": "1",
     *      "firstName": "John",
     *      "lastName": "Doe",
     *      "image": "http://someImageUrl/john.jpg"
     *    },
     *    {
     *      "_id": "2",
     *      "firstName": "Jane",
     *      "lastName": "Doe",
     *      "image": "http://someImageUrl/jane.jpg"
     *    }
     *  ]
     *  ```
     *
     *  Repeater data is not automatically applied to the elements in the repeated
     *  items. You choose how to use the repeater's data in the [`onItemReady()`](#onItemReady),
     *  [`onItemRemoved()`](#onItemRemoved), [`forItems()`](#forItems), and
     *  [`forEachItem()`](#forEachItem) callback functions. Most often, you
     *  apply the data of a repeated item to the properties and functions
     *  of the repeated elements contained in that repeated item. 
     * 
     *  Typically, you use the [`onItemReady()`](#onItemReady) function to set a callback
     *  that populates the repeater's elements with the item data from the `data` array.
     *  The callback is triggered for each new item in the `data` array as described below.
     *  Then, you might use the [`forItems()`](#forItems) and [`forEachItem()`](#forEachItem)
     *  functions to modify repeater elements at some point after the `data` is originally set.
     * 
     *  Because setting a repeater's `data` property triggers the `onItemReady()`
     *  callback to run, make sure you call `onItemReady()` before you set the
     *  `data` property. Failing to do so will mean that your callbacks are not
     *  triggered when you set the `data` property.
     *
     *  You cannot modify the data array in-place. To add, change, or remove
     *  objects from the repeater's data array:
     *
     *    1. Store the value of the `data` property in a variable.
     *    1. Make changes to the objects of the data array.
     *    1. Reset the `data` property with the modified array.
     *
     *
     *  When the repeater's `data` property is set:
     *
     *    1. New repeated items are created for each object that has an `_id` value
     *       that is not already present in the current array of data objects. The elements
     *       in the new items are first populated with the data of the repeater's
     *       [item template](#repeated-item-template). Then the [`onItemReady()`](#onItemReady)
     *       event handler is triggered for each of the new items. Usually, you
     *       overwrite some or all of the data populated from the item template
     *       in the [`onItemReady()`](#onItemReady) event handler with the data for
     *       that specific item. When all of the [`onItemReady()`](#onItemReady)
     *       event handlers have finished running, the new items are displayed.
     *    1. Repeated items are removed if their IDs are no longer in the array of
     *       data objects. The [`onItemRemoved()`](#onItemRemoved) event handler is
     *       triggered for each of the removed items.
     *    1. Nothing occurs to repeated items whose IDs were already in the array
     *       of data objects, even if other data in the object has changed. To update
     *       repeated items with the new data, use the [`forEachItem()`](#forEachItem)
     *       or [`forItems()`](#forItems) functions.
     *
     *
     *  Getting the `data` property returns the repeater's current data. If you
     *  have not yet explicitly set the repeater's `data`, getting the `data`
     *  property returns only the IDs of the current repeated items that were
     *  set in the Editor.
     * @snippet [Repeater-data_get.es6=Get a repeater's data]
     * @snippet [Repeater-data_set.es6=Set a repeater's data]
     * @snippet [Repeater-data_set-query.es6=Set a repeater's data from a database query]
     * @snippet [Repeater-data_change.es6=Modify a repeater's data]
     * @snippet [Repeater-data_set-scenario.es6=Set a repeater's data]
     *  This example defines an event handler to handle the creation of new items
     *  and sets a repeater's data.
     * @snippet [Repeater-data_set-scenario-add.es6=Set a repeater's data and add new data on a button click]
     *  This example defines an event handler to handle the creation of new items
     *  and sets a repeater's data. It also adds an event handler to an
     *  "addButton" that is not part of the repeater. Clicking the button resets
     *  the repeater's data, adding another item.
     * @snippet [Repeater-data_set-scenario-remove.es6=Set the a repeater's data and remove some data on a button click]
     *  This example defines event handlers to handle the creation of new items
     *  and the removal of existing items. Then it sets a repeater's data.
     *  Lastly, it adds an event handler to a "removeButton" that is not part of
     *  the repeater. Clicking the button resets the repeater's data, removing
     *  one of the items.
     * @type {Object[]}
     * @memberof $w.Repeater
     */
    get data() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return getRepeaterOrDataItems(RMI, compId);
    }

  /**
   * @member data
   * @memberof $w.Repeater
   * @param {Object[]} items
   */
    set data(items) {
        const RMI = members._RMI.get(this);
        const compType = members._type.get(this).compType;
        const compId = members._compId.get(this);

        if (!validate({
            compType,
            functionName: 'data',
            dataToValidate: {data: items},
            compName: this.id
        }) ||
            !validateArrayContainsItemsWithIds({items}) ||
            !_.every(items, (item, index) => validateItemIdFormatAndReportIfNecessary(item._id, index, this.id) && validate({
                compType,
                functionName: 'data',
                dataToValidate: {_id: item._id},
                compName: this.id
            }))) {
            return;
        }
        if (platformUtils.getRGI().getLivePreviewMode() && items.length > MAX_ITEMS_EDITOR) {
            items = items.slice(0, MAX_ITEMS_EDITOR);
            const appDefId = RMI.getAppDefinitionId();
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.NOTIFY_EVENT_TO_EDITOR_APP,
                data: {eventData: {eventType: 'repeaterEditorMaxItems'},
                    appDefinitionId: appDefId
                },
                appDefinitionId: appDefId
            });
        }
        const contextId = members._contextId.get(this);
        const wixSelector = members._wixSelector.get(this);
        const compData = RMI.getData(compId);
        const shouldTriggerOnItemRemoved = !!compData.fullItems;
        let currentItems = getRepeaterOrDataItems(RMI, compId);
        const uniqueItems = _.uniqBy(items, '_id');
        currentItems = clearPrevStateIfNeeded(currentItems, uniqueItems);
        const repeaterUtils = platformUtils.getRepeaterUtils();
        const addedItems = repeaterUtils.items.getAddedItems(currentItems, uniqueItems);
        const removedItems = repeaterUtils.items.getRemovedItems(currentItems, uniqueItems);
        const removedComps = getCompIdToCompType(RMI, compId, removedItems);
        const itemIds = _.uniq(_.map(items, '_id'));

        RMI.startTransaction();
        RMI.setDataWithoutUpdate(compId, {fullItems: uniqueItems});
        RMI.setData(compId, {items: itemIds});

        const addedComps = getCompIdToCompType(RMI, compId, addedItems);
        compInitializeService.componentsInitialize(RMI, contextId, addedComps);
        compInitializeService.componentsRemoval(RMI, contextId, removedComps);

        executeBehaviorByTypeForItems(RMI, wixSelector, compId, RMI.getEventTypes().ITEM_READY, addedItems, true, this.id, 'onItemReady');
        if(shouldTriggerOnItemRemoved) {
            executeBehaviorByTypeForItems(RMI, wixSelector, compId, RMI.getEventTypes().ITEM_REMOVED, removedItems, false, this.id, 'onItemRemoved');
        }
        RMI.endTransaction();
    }

    /**
     * @callback $w.ItemReadyEventHandler
     * @description Handles repeater item ready events.
     * @param {$w.$w} $item A selector function with [repeated item scope](#repeated-item-scope).
     * @param {Object} itemData The object from the repeater's `data` array that corresponds to the repeated item being created.
     * @param {Number} index The index of the `itemData` object in the `data` array.
     */

    /**
     * @function onItemReady
     * @summary Sets the function that runs when a new repeated item is created.
     * @description
     *  Use the `onItemReady()` function for code you want to run before new
     *  repeated items are rendered. 
     *
     *  The callback is triggered when you add new items by setting the
     *  [`data`](#data) property or when new items are created based on a
     *  dataset that is connected to the repeater. It is not triggered for
     *  existing items that are updated when you set the [`data`](#data)
     *  property. (For more information about when new items are created, see
     *  the `data` property.) To run code after updating existing items, use
     *  the [`forEachItem`](#forEachItem) or [`forItems`](#forItems) functions
     *  instead.
     * 
     *  Because setting a repeater's `data` property triggers the `onItemReady()`
     *  callback to run, make sure you call `onItemReady()` before you set the
     *  `data` property. Failing to do so will mean that your callbacks are not
     *  triggered when you set the `data` property.
     *
     *  Usually, you use `onItemReady()` to:
     *
     *  + Apply the repeated item's `itemData` to the properties and functions
     *    of the repeated elements contained in the repeated item being created.
     *
     *    For example, you can use `onItemReady()` to set the [`src`]($w.Image.html#src)
     *    property of an image in the repeater to an image source found in the `itemData`
     *    object and then [`show()`]($w.Image.html#show) the image.
     *  + Add event handlers to the repeated elements contained in
     *    the repeated item being created.
     *
     *    For example, you can use `onItemReady()` to set the [`onClick()`]($w.Button.html#onClick)
     *    event handler of a button in the repeater.
     * 
     * > **Note:** When using a dataset to populate the contents of your repeated items,
     *   the `onItemReady()` callback function is triggered before the dataset
     *   populates the values of your page elements. Therefore, element values
     *   that you set using `onItemReady()` may be overridden when the dataset is
     *   ready. To change the values set by the dataset, use [`forEachItem`](#forEachItem)
     *   inside the dataset's [`onReady()`](wix-dataset.html#onReady). For more
     *   information, see the [`forEachItem`](#forEachItem) examples.
     * @snippet [Repeater-onItemReady.es6=Set up new repeated items]
     * @snippet [Repeater-onItemReady_scenario.es6=Set up new repeated items]
     *  This example creates an event handler that sets up new repeated items by
     *  setting the source of a repeated image element and the text value of a
     *  repeated text element to values from the repeated item's data.
     *
     *  It also sets an event handler that runs when the image in a repeated
     *  item is clicked. The event handler changes the value of a repeated text
     *  element to indicate whether the repeated item is "selected". It also
     *  changes the value of a non-repeated text element which serves as a
     *  counter for how many repeated items are "selected".
     * @snippet [Repeater-onItemReady_data.es6=Trigger `onItemReady()` by setting a repeater's data]
     *  This example demonstrates setting the data property of a repeater, 
     *  which triggers the onItemReady function.
     *  
     *  The site visitor enters search text and when they click 
     *  a Search button, a query runs that returns the results for that query. 
     *  The results of that query are displayed in a repeater. A message
     *  is displayed if there are no results. 
     *  
     *  When the data property is set for the repeater, the onItemReady function is 
     *  automatically triggered.
     * @param {$w.ItemReadyEventHandler} handler The name of the function or
     *  the function expression to run when the item is ready.
     * @returns {$w.Repeater} The element on which the event is now registered.
     * @eventtype itemReady
     * @eventhandler
     * @memberof $w.Repeater
     */

    onItemReady(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onItemReady',
            dataToValidate: {callback: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const controllerId = members._controllerId.get(this);
        const contextId = members._contextId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ITEM_READY, controllerId, cb);

        /*Trigger onItemReady on existing items, only after the first set items()*/
        const compData = RMI.getData(compId);
        if(compData.fullItems) {
            const currentItems = getRepeaterOrDataItems(RMI, compId);
            const wixSelector = members._wixSelector.get(this);
            const newBehavior = [{cb, contextId, controllerId}];
            const itemsWithIndex = _.map(currentItems, (item, index) => ({item, index}));

            executeBehaviorForItems(wixSelector, compId, newBehavior, itemsWithIndex, true, this.id, 'onItemReady');
        }

        return this;
    }

    /**
     * @callback $w.ItemRemovedEventHandler
     * @description Handles repeater item removed events.
     * @param {Object} itemData The object from the repeater's `data` array that corresponds to the repeated item being removed.
     */

    /**
     * @function onItemRemoved
     * @summary Sets the function that runs when a repeated item is removed.
     * @description
     *  Use the `onItemRemoved()` function for code you want to run when
     *  repeated items are removed. The callback is triggered when you remove
     *  items by setting the value of the [`data`](#data) property to an array
     *  in which some of the existing item IDs are no longer present or when items
     *  are removed based on a dataset that is connected to the repeater.
     *
     * > **Note:** The `onItemRemoved()` callback function is not called when the static
     *  repeated items that were set in the Editor are removed.
     * @snippet [Repeater-onItemRemoved.es6=Get data from items being removed]
     * @param {$w.ItemRemovedEventHandler} handler The name of the function or
     *  the function expression to run when the item is removed.
     * @returns {$w.Repeater} The element on which the event is now registered.
     * @eventtype itemRemoved
     * @eventhandler
     * @memberof $w.Repeater
     */
    onItemRemoved(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onItemRemoved',
            dataToValidate: {callback: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const controllerId = members._controllerId.get(this);
        const contextId = members._contextId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().ITEM_REMOVED, controllerId, cb);

        return this;
    }

    /**
     * @callback $w.ForItemCallback
     * @description Runs for items in a repeater.
     * @param {$w.$w} $item A selector function with [repeated item scope](#repeated-item-scope).
     * @param {Object} itemData The object from the repeater's `data` array that corresponds to the current repeated item.
     * @param {Number} index The index of the `itemData` object in the `data` array.
     */

    /**
     * @function forEachItem
     * @summary Runs a function for each repeated item.
     * @description
     *  Use the `forEachItem()` function to run a function on all of a repeater's
     *  repeated items. You can use the callback function to update or pull
     *  information from all of the repeater's repeated items.
     *
     *  When you set a repeater's [`data`](#data) property with data that changes
     *  items with existing IDs, those changes are not automatically reflected
     *  in the elements contained in the repeater. That is because you are
     *  responsible for applying a repeater's data to its repeated items.
     *
     *  To apply the data to items with new IDs, you can use the [`onItemReady()`](#onItemReady)
     *  event handler.
     *
     *  To update items with existing IDs, you can use the `forEachItem()` or
     *  [`forItems()`](#forItems) functions.
     *
     *  Usually, when updating repeated items you:
     *
     *  + Apply the repeated item's `itemData` to the properties and functions
     *    of the repeated elements contained in the repeated item being created.
     *
     *    For example, you can use `onItemReady()` to set the [`src`]($w.Image.html#src)
     *    property of an image in the repeater to an image source found in the `itemData`
     *    object and then [`show()`]($w.Image.html#show) the image.
     *  + Add event handlers to the repeated elements contained in
     *    the repeated item being created.
     *
     *    For example, you can use `onItemReady()` to set the [`onClick()`]($w.Button.html#onClick)
     *    event handler of a button in the repeater.
     * @snippet [Repeater-forEachItem.es6=Loop through all of a repeater's repeated items]
     * @snippet [Repeater-forEachItem_scenario.es6=Update data in all of a repeater's repeated items]
     * @snippet [Repeater-forEachItem_dataset_override.es6=Update data in all of a repeater's repeated items]
     *  This example uses a `forEachItem()` to override some of the data populated
     *  into a repeater that is connected to a dataset. Here we want a text element
     *  to contain text that changes based on the value of a certain boolean field.
     *  When a page loads, the dataset loads after the repeater. So we wait for the
     *  dataset's [`onReady()`](wix-dataset.html#onReady) to call a `forEachItem()`
     *  that sets the desired values.
     * @param {$w.ForItemCallback} callback The name of the function or the function expression to run for each repeated item.
     * @memberof $w.Repeater
     */
    forEachItem(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'forEachItem',
            dataToValidate: {callback: cb},
            compName: this.id
        })) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const repeaterItems = getRepeaterOrDataItems(RMI, compId);
        const wixSelector = members._wixSelector.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);

        _.forEach(repeaterItems, (itemData, index) => {
            try {
                cb(wixSelector.getScopedSelectorForDisplayedOnlyComp(compId, itemData._id, contextId, controllerId), itemData, index);
            } catch(error) {
                throw new monitoring.MonitoringErrorToIgnore(error);
            }
        });
    }

    /**
     * @function forItems
     * @summary Runs a function for each repeated item with the given IDs.
     * @description
     *  Use the `forItems()` function to run a function on a specified list of
     *  repeated items. You can use the callback function to update or pull
     *  information from the specified repeated items.
     *
     *  When you set a repeater's [`data`](#data) property with data that changes
     *  items with existing IDs, those changes are not automatically reflected
     *  in the elements contained in the repeater. That is because you are
     *  responsible for applying a repeater's data to its repeated items.
     *
     *  To apply the data to items with new IDs, you can use the [`onItemReady()`](#onItemReady)
     *  event handler.
     *
     *  To update items with existing IDs, you can use the `forEachItem()` or
     *  `forItems()` functions.
     *
     *  Usually, when updating repeated items you:
     *
     *  + Apply the repeated item's `itemData` to the properties and functions
     *    of the repeated elements contained in the repeated item being created.
     *
     *    For example, you can use `onItemReady()` to set the [`src`]($w.Image.html#src)
     *    property of an image in the repeater to an image source found in the `itemData`
     *    object and then [`show()`]($w.Image.html#show) the image.
     *  + Add event handlers to the repeated elements contained in
     *    the repeated item being created.
     *
     *    For example, you can use `onItemReady()` to set the [`onClick()`]($w.Button.html#onClick)
     *    event handler of a button in the repeater.
     * @snippet [Repeater-forItems.es6=Loop through some of a repeater's repeated items]
     * @snippet [Repeater-forItems_scenario.es6=Update data in some of a repeater's repeated items]
     * @param {String[]} itemIds The IDs of the items on which to run the callback function.
     * @param {$w.ForItemCallback} callback The name of the function or the function expression to run for each repeated item.
     * @memberof $w.Repeater
     */
    forItems(ids, cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'forItems',
            dataToValidate: {callback: cb, ids},
            compName: this.id
        })) {
            return;
        }

        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const repeaterItems = getRepeaterOrDataItems(RMI, compId);
        const wixSelector = members._wixSelector.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);

        const existItems = _.transform(ids, (result, id) => {
            const itemIndex = _.findIndex(repeaterItems, {_id: id});
            if(itemIndex === -1){
                reporter.reportSdkWarning(generateError.warning_non_existing_item({
                    functionName: 'forItems',
                    _id: id
                }));

                return;
            }

            result.push({itemData: repeaterItems[itemIndex], index: itemIndex});
        }, []);

        _.forEach(existItems, (item) => {
            try {
                cb(wixSelector.getScopedSelectorForDisplayedOnlyComp(compId, item.itemData._id, contextId, controllerId), item.itemData, item.index);
            } catch(error) {
                throw new monitoring.MonitoringErrorToIgnore(error);
            }
        });
    }

    toJSON() {
        const {id, type, global, rendered, items} = this;
        return {id, type, global, rendered, items};
    }
}

mixin(Repeater.prototype, HiddenCollapsedMixin.prototype);

export default Repeater;
